import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useHotkeys } from 'react-hotkeys-hook';

/**
 * VideoMagnifier - A high-performance, reusable component that adds magnification functionality to video and image elements
 * 
 * Features:
 * - Magnification lens with configurable size (100-500px) and level (1-9x)
 * - Sticky edges with hysteresis that prevent accidentally leaving the media element
 * - Visual feedback (white/red/orange borders) when at buffer edge or breaking out
 * - Viewport boundary detection to keep lens visible
 * - Optimized performance with canvas reuse, cached DOM calls, and throttled tracking
 * - Keyboard shortcuts (1-9, w, q, e, esc) via react-hotkeys-hook
 * - Support for video, image, or both element types
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - The media container to wrap (typically a div containing the video/image element)
 * @param {React.RefObject<HTMLVideoElement|HTMLImageElement>} props.mediaRef - **Ref to the actual <video> or <img> element** - used for frame capture and dimension calculations
 * @param {React.RefObject<HTMLElement>} [props.containerRef] - **Optional ref to the parent container** - used for positioning the lens. If not provided, mediaRef is used for positioning.
 * @param {'video'|'image'|'both'} [props.targetType='both'] - Type of elements to apply magnification to: 'video', 'image', or 'both'
 * @param {boolean} [props.enabled=false] - Whether magnification is enabled
 * @param {number} [props.level=2] - Magnification level (1-9)
 * @param {number} [props.lensSize=200] - Size of magnification lens in pixels (100-500)
 * @param {number} [props.frameRate=20] - Frame capture rate in fps (for video) or refresh rate (for images)
 * @param {number} [props.breakoutMargin=25] - Pixels to push past edge to break free
 * @param {(newLevel: number) => void} [props.onLevelChange] - Callback when magnification level changes
 * @param {(newSize: number) => void} [props.onLensSizeChange] - Callback when lens size changes
 * 
 * @example
 * ```jsx
 * // For video elements
 * <VideoMagnifier 
 *   mediaRef={videoRef} 
 *   containerRef={containerRef}
 *   targetType="video"
 *   enabled={true}
 *   level={2}
 *   lensSize={200}
 *   onLevelChange={setLevel}
 *   onLensSizeChange={setLensSize}
 * >
 *   <div ref={containerRef}>
 *     <video ref={videoRef} src="..." />
 *   </div>
 * </VideoMagnifier>
 * 
 * // For image elements
 * <VideoMagnifier 
 *   mediaRef={imageRef} 
 *   targetType="image"
 *   enabled={true}
 * >
 *   <img ref={imageRef} src="..." />
 * </VideoMagnifier>
 * 
 * // For both (will work on any video or image)
 * <VideoMagnifier 
 *   mediaRef={mediaRef} 
 *   targetType="both"
 *   enabled={true}
 * >
 *   <div>
 *     <video ref={mediaRef} src="..." />
 *     <img ref={mediaRef} src="..." />
 *   </div>
 * </VideoMagnifier>
 * ```
 */
const VideoMagnifier = ({
  children,
  mediaRef,
  containerRef = null,
  targetType = 'both',
  enabled = false,
  level = 2,
  lensSize = 200,
  frameRate = 20,
  breakoutMargin = 25,
  onLevelChange,
  onLensSizeChange
}) => {
  // Use mediaRef as fallback if containerRef not provided
  const effectiveContainerRef = containerRef || mediaRef;

  // Store initial values for reset
  const initialLevelRef = useRef(level);
  const initialLensSizeRef = useRef(lensSize);

  const [isHovering, setIsHovering] = useState(false);
  const [lensPosition, setLensPosition] = useState({ x: 0, y: 0 });
  const [backgroundPosition, setBackgroundPosition] = useState({ x: 0, y: 0, effectiveZoom: level });
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [isLocked, setIsLocked] = useState(true);
  const [breakoutProgress, setBreakoutProgress] = useState(0);
  const animationFrameRef = useRef(null);
  const mouseMoveThrottleRef = useRef(null);
  const canvasRef = useRef(null); // Reuse canvas instead of creating new one each frame
  const lastRectCache = useRef({ container: null, video: null, timestamp: 0 });
  const lastMouseEventRef = useRef(null); // Store last mouse event for hotkey updates

  // Use refs for values that change frequently to avoid recreating callbacks
  const lensSizeRef = useRef(lensSize);
  const levelRef = useRef(level);
  const breakoutMarginRef = useRef(breakoutMargin);

  // Keep refs in sync with props
  useEffect(() => {
    lensSizeRef.current = lensSize;
    levelRef.current = level;
    breakoutMarginRef.current = breakoutMargin;
  }, [lensSize, level, breakoutMargin]);

  // Update lens when level or lensSize changes (for hotkey support)
  useEffect(() => {
    if (isHovering && enabled && lastMouseEventRef.current) {
      // Trigger a lens update with the last known mouse position
      handleMouseMove(lastMouseEventRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level, lensSize, isHovering, enabled]);

  // Keyboard shortcuts for magnification control using react-hotkeys-hook
  // Numbers 1-9: Set magnification level
  useHotkeys('1,2,3,4,5,6,7,8,9', (e) => {
    const newLevel = parseInt(e.key);
    if (onLevelChange) onLevelChange(newLevel);
  }, { enabled }, [enabled, onLevelChange]);

  // Backtick (`): Reset magnification level to 2
  useHotkeys('w', () => {
    if (onLevelChange) onLevelChange(2);
  }, { enabled }, [enabled, onLevelChange]);

  // E: Increase lens size by 25px
  useHotkeys('e', (e) => {
    e.preventDefault();
    const newSize = Math.min(lensSizeRef.current + 25, 500);
    if (onLensSizeChange) onLensSizeChange(newSize);
  }, { enabled }, [enabled, onLensSizeChange]);

  // Q: Decrease lens size by 25px
  useHotkeys('q', (e) => {
    e.preventDefault();
    const newSize = Math.max(lensSizeRef.current - 25, 100);
    if (onLensSizeChange) onLensSizeChange(newSize);
  }, { enabled }, [enabled, onLensSizeChange]);

  // Escape: Reset to initial values
  useHotkeys('escape', (e) => {
    e.preventDefault();
    if (onLevelChange) onLevelChange(initialLevelRef.current);
    if (onLensSizeChange) onLensSizeChange(initialLensSizeRef.current);
  }, { enabled }, [enabled, onLevelChange, onLensSizeChange]);

  // Global mouse tracking when hovering
  useEffect(() => {
    if (!isHovering || !enabled || !mediaRef) return;

    const handleGlobalMouseMove = (e) => {
      // Check if we're still within the breakout zone
      if (mediaRef && mediaRef.current) {
        const mediaRect = mediaRef.current.getBoundingClientRect();
        const x = e.clientX - mediaRect.left;
        const y = e.clientY - mediaRect.top;

        const isWithinBreakout =
          x >= -breakoutMarginRef.current &&
          x <= mediaRect.width + breakoutMarginRef.current &&
          y >= -breakoutMarginRef.current &&
          y <= mediaRect.height + breakoutMarginRef.current;

        if (!isWithinBreakout) {
          setIsHovering(false);
          setIsLocked(true);
          setBreakoutProgress(0);
          return;
        }
      }

      // Call handleMouseMove if it exists
      if (typeof handleMouseMove === 'function') {
        handleMouseMove(e);
      }
    };

    window.addEventListener('mousemove', handleGlobalMouseMove);
    return () => window.removeEventListener('mousemove', handleGlobalMouseMove);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHovering, enabled, breakoutMargin, mediaRef]);

  // Capture media frames for magnification when hovering (optimized)
  useEffect(() => {
    if (!isHovering || !enabled || !mediaRef || !mediaRef.current) {
      setBackgroundImage(null);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      return;
    }

    const mediaElement = mediaRef.current;
    const isVideo = mediaElement.tagName === 'VIDEO';
    const isImage = mediaElement.tagName === 'IMG';

    // Check if target type matches the element
    const shouldProcess =
      targetType === 'both' ||
      (targetType === 'video' && isVideo) ||
      (targetType === 'image' && isImage);

    if (!shouldProcess) {
      setBackgroundImage(null);
      return;
    }

    // For images, capture once and update on refresh interval
    if (isImage) {
      // Check if image is loaded
      if (!mediaElement.complete || mediaElement.naturalWidth === 0) {
        // Image not loaded yet, wait for it to load
        const handleImageLoad = () => {
          // Trigger a re-render by setting a dummy state
          setBackgroundImage(null);
        };
        mediaElement.addEventListener('load', handleImageLoad, { once: true });
        return () => {
          mediaElement.removeEventListener('load', handleImageLoad);
        };
      }

      let lastUpdate = 0;
      const updateInterval = 1000 / frameRate;

      const updateFrame = (timestamp) => {
        if (timestamp - lastUpdate >= updateInterval) {
          try {
            if (!mediaRef.current) return;

            // Reuse canvas instead of creating new one
            if (!canvasRef.current) {
              canvasRef.current = document.createElement('canvas');
            }
            const canvas = canvasRef.current;
            const width = mediaElement.naturalWidth || mediaElement.width || 640;
            const height = mediaElement.naturalHeight || mediaElement.height || 480;

            // Only resize canvas if dimensions changed
            if (canvas.width !== width || canvas.height !== height) {
              canvas.width = width;
              canvas.height = height;
            }

            const ctx = canvas.getContext('2d', { alpha: false, willReadFrequently: false });
            ctx.drawImage(mediaElement, 0, 0, width, height);

            try {
              const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
              setBackgroundImage(dataUrl);
            } catch (toDataURLError) {
              // CORS issue - try with PNG or handle gracefully
              console.warn('VideoMagnifier: CORS issue with image, trying PNG', toDataURLError);
              try {
                const dataUrl = canvas.toDataURL('image/png');
                setBackgroundImage(dataUrl);
              } catch (pngError) {
                console.error('VideoMagnifier: Unable to convert canvas to data URL', pngError);
              }
            }
          } catch (error) {
            console.warn('VideoMagnifier: Unable to capture image', error);
          }
          lastUpdate = timestamp;
        }
        animationFrameRef.current = requestAnimationFrame(updateFrame);
      };

      animationFrameRef.current = requestAnimationFrame(updateFrame);
    } else if (isVideo) {
      // For videos, capture frames continuously
      let lastUpdate = 0;
      const updateInterval = 1000 / frameRate;

      const updateFrame = (timestamp) => {
        if (timestamp - lastUpdate >= updateInterval) {
          try {
            if (!mediaRef.current) return;

            // Reuse canvas instead of creating new one
            if (!canvasRef.current) {
              canvasRef.current = document.createElement('canvas');
            }
            const canvas = canvasRef.current;
            const width = mediaElement.videoWidth || 640;
            const height = mediaElement.videoHeight || 480;

            // Only resize canvas if dimensions changed
            if (canvas.width !== width || canvas.height !== height) {
              canvas.width = width;
              canvas.height = height;
            }

            const ctx = canvas.getContext('2d', { alpha: false, willReadFrequently: false });
            ctx.drawImage(mediaElement, 0, 0, width, height);
            setBackgroundImage(canvas.toDataURL('image/jpeg', 0.8));
          } catch (error) {
            console.warn('VideoMagnifier: Unable to capture video frame', error);
          }
          lastUpdate = timestamp;
        }
        animationFrameRef.current = requestAnimationFrame(updateFrame);
      };

      animationFrameRef.current = requestAnimationFrame(updateFrame);
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isHovering, enabled, mediaRef, frameRate, targetType]);

  const handleMouseMove = useCallback((e) => {
    if (!enabled || !effectiveContainerRef || !effectiveContainerRef.current || !mediaRef || !mediaRef.current) return;

    // Store the last mouse event for hotkey updates
    lastMouseEventRef.current = e;

    // Throttle mouse move to every 16ms (~60fps max)
    if (mouseMoveThrottleRef.current) return;
    mouseMoveThrottleRef.current = requestAnimationFrame(() => {
      mouseMoveThrottleRef.current = null;

      // Cache getBoundingClientRect calls (expensive operation)
      const now = performance.now();
      if (!lastRectCache.current.container || now - lastRectCache.current.timestamp > 100) {
        lastRectCache.current = {
          container: effectiveContainerRef.current.getBoundingClientRect(),
          media: mediaRef.current.getBoundingClientRect(),
          timestamp: now
        };
      }
      const rect = lastRectCache.current.container;
      const mediaRect = lastRectCache.current.media;
      let x = e.clientX - rect.left;
      let y = e.clientY - rect.top;

      // Calculate position relative to the actual media element
      let mediaX = e.clientX - mediaRect.left;
      let mediaY = e.clientY - mediaRect.top;

      // Calculate the offset of the rendered media within the media element's bounding box
      // This accounts for letterboxing (black bars top/bottom) or pillarboxing (black bars left/right)
      const mediaElement = mediaRef.current;
      let mediaOffsetX = 0;
      let mediaOffsetY = 0;
      let renderedWidth = mediaRect.width;
      let renderedHeight = mediaRect.height;

      if (mediaElement) {
        let mediaAspect;
        if (mediaElement.tagName === 'VIDEO' && mediaElement.videoWidth && mediaElement.videoHeight) {
          mediaAspect = mediaElement.videoWidth / mediaElement.videoHeight;
        } else if (mediaElement.tagName === 'IMG' && mediaElement.naturalWidth && mediaElement.naturalHeight) {
          mediaAspect = mediaElement.naturalWidth / mediaElement.naturalHeight;
        }

        if (mediaAspect) {
          const elementAspect = mediaRect.width / mediaRect.height;

          if (Math.abs(mediaAspect - elementAspect) > 0.01) {
            if (mediaAspect > elementAspect) {
              // Media is wider - letterboxing on top/bottom
              renderedHeight = mediaRect.width / mediaAspect;
              mediaOffsetY = (mediaRect.height - renderedHeight) / 2;
            } else {
              // Media is taller - pillarboxing on left/right
              renderedWidth = mediaRect.height * mediaAspect;
              mediaOffsetX = (mediaRect.width - renderedWidth) / 2;
            }
          }
        }
      }

      // Adjust mouse position to account for media offset within element
      mediaX = mediaX - mediaOffsetX;
      mediaY = mediaY - mediaOffsetY;

      // Calculate effective zoom level to ensure content fills the lens
      // If the magnified content is smaller than the lens, increase zoom
      const minZoomForWidth = lensSizeRef.current / renderedWidth;
      const minZoomForHeight = lensSizeRef.current / renderedHeight;
      const minRequiredZoom = Math.max(minZoomForWidth, minZoomForHeight);
      const effectiveZoom = Math.max(levelRef.current, minRequiredZoom);

      // Calculate dynamic sticky margin based on lens size and effective magnification
      // When the edge of the magnifier shows the edge of the picture, that's the buffer stop point
      const stickyMargin = lensSizeRef.current / (2 * effectiveZoom); // Half lens size divided by effective magnification level

      // Calculate how far past the sticky edge we are
      let pushDistance = 0;
      if (mediaX < stickyMargin) {
        pushDistance = stickyMargin - mediaX;
      } else if (mediaX > renderedWidth - stickyMargin) {
        pushDistance = mediaX - (renderedWidth - stickyMargin);
      }
      if (mediaY < stickyMargin) {
        pushDistance = Math.max(pushDistance, stickyMargin - mediaY);
      } else if (mediaY > renderedHeight - stickyMargin) {
        pushDistance = Math.max(pushDistance, mediaY - (renderedHeight - stickyMargin));
      }

      // Calculate breakout progress
      // When at buffer edge (pushDistance = 0), progress = -1
      // When pushing to break out (pushDistance > 0), progress = 0 to 1
      const progress =
        pushDistance > 0
          ? Math.min(pushDistance / breakoutMarginRef.current, 1)
          : pushDistance === 0 &&
            (mediaX <= stickyMargin ||
              mediaX >= renderedWidth - stickyMargin ||
              mediaY <= stickyMargin ||
              mediaY >= renderedHeight - stickyMargin)
            ? -1
            : 0;

      if (
        Math.abs(progress - breakoutProgress) > 0.05 ||
        (progress === -1 && breakoutProgress !== -1) ||
        (progress === 0 && breakoutProgress === -1)
      ) {
        setBreakoutProgress(progress);
      }

      // Check if outside bounds
      const isOutside =
        mediaX < -breakoutMarginRef.current ||
        mediaX > mediaRect.width + breakoutMarginRef.current ||
        mediaY < -breakoutMarginRef.current ||
        mediaY > mediaRect.height + breakoutMarginRef.current;

      if (isOutside && isLocked) {
        setIsLocked(false);
        setBreakoutProgress(0);
      } else if (
        !isOutside &&
        mediaX >= 0 &&
        mediaX <= mediaRect.width &&
        mediaY >= 0 &&
        mediaY <= mediaRect.height &&
        !isLocked
      ) {
        setIsLocked(true);
        setBreakoutProgress(0);
      }

      // Apply sticky constraint if locked
      if (isLocked) {
        mediaX = Math.max(stickyMargin, Math.min(mediaX, renderedWidth - stickyMargin));
        mediaY = Math.max(stickyMargin, Math.min(mediaY, renderedHeight - stickyMargin));
        x = mediaRect.left - rect.left + mediaX + mediaOffsetX;
        y = mediaRect.top - rect.top + mediaY + mediaOffsetY;
      }

      // Update positions with viewport boundary checking
      let lensX = x - lensSizeRef.current / 2;
      let lensY = y - lensSizeRef.current / 2;

      // Get viewport dimensions
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const containerRect = rect;

      // Calculate lens position in viewport coordinates
      const lensViewportX = containerRect.left + lensX;
      const lensViewportY = containerRect.top + lensY;

      // Constrain lens to viewport bounds
      if (lensViewportX < 0) {
        lensX = -containerRect.left;
      } else if (lensViewportX + lensSizeRef.current > viewportWidth) {
        lensX = viewportWidth - containerRect.left - lensSizeRef.current;
      }

      if (lensViewportY < 0) {
        lensY = -containerRect.top;
      } else if (lensViewportY + lensSizeRef.current > viewportHeight) {
        lensY = viewportHeight - containerRect.top - lensSizeRef.current;
      }

      setLensPosition({
        x: lensX,
        y: lensY,
      });

      setBackgroundPosition({
        x: -(mediaX * effectiveZoom - lensSizeRef.current / 2),
        y: -(mediaY * effectiveZoom - lensSizeRef.current / 2),
        effectiveZoom, // Store effective zoom for rendering
      });
    });
  }, [enabled, effectiveContainerRef, mediaRef, isLocked, breakoutProgress]);

  const handleMouseEnter = useCallback(() => {
    if (enabled) {
      setIsHovering(true);
      setIsLocked(true);
      setBreakoutProgress(0);
    }
  }, [enabled]);

  const handleMouseLeave = useCallback((e) => {
    // When leaving the media element, check if we're still within breakout margin
    if (!effectiveContainerRef || !effectiveContainerRef.current || !mediaRef || !mediaRef.current) {
      setIsHovering(false);
      setIsLocked(true);
      setBreakoutProgress(0);
      return;
    }

    const mediaRect = mediaRef.current.getBoundingClientRect();
    const x = e.clientX - mediaRect.left;
    const y = e.clientY - mediaRect.top;

    // Check if we're within the breakout margin
    const isWithinBreakout =
      x >= -breakoutMarginRef.current &&
      x <= mediaRect.width + breakoutMarginRef.current &&
      y >= -breakoutMarginRef.current &&
      y <= mediaRect.height + breakoutMarginRef.current;

    if (!isWithinBreakout) {
      setIsHovering(false);
      setIsLocked(true);
      setBreakoutProgress(0);
    }
  }, [effectiveContainerRef, mediaRef]);

  return (
    <>
      <div
        style={{ display: 'inline-block' }}
        onMouseMove={enabled ? handleMouseMove : undefined}
        onMouseEnter={enabled ? handleMouseEnter : undefined}
        onMouseLeave={enabled ? handleMouseLeave : undefined}
      >
        {children}
      </div>

      {/* Magnification Lens - Rendered via Portal to avoid clipping */}
      {enabled &&
        isHovering &&
        backgroundImage &&
        effectiveContainerRef &&
        effectiveContainerRef.current &&
        mediaRef &&
        mediaRef.current &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              left: `${effectiveContainerRef.current.getBoundingClientRect().left + lensPosition.x}px`,
              top: `${effectiveContainerRef.current.getBoundingClientRect().top + lensPosition.y}px`,
              width: `${lensSize}px`,
              height: `${lensSize}px`,
              borderRadius: '8px',
              overflow: 'hidden',
              backgroundColor: '#1a1a1a', // Dark background to fill empty space
              border:
                breakoutProgress === -1
                  ? '3px solid rgba(255, 0, 0, 0.9)' // Red when at buffer edge
                  : breakoutProgress > 0
                    ? `3px solid rgba(255, ${Math.floor(155 - breakoutProgress * 155)}, 0, 0.9)` // Orange when pushing to break out
                    : '3px solid rgba(255, 255, 255, 0.9)', // White when not at edge
              boxShadow:
                breakoutProgress > 0
                  ? `0 4px 16px rgba(0, 0, 0, 0.6), 0 0 ${Math.floor(breakoutProgress * 20)}px rgba(255, ${150 - Math.floor(breakoutProgress * 150)}, 0, ${breakoutProgress * 0.8})`
                  : '0 4px 16px rgba(0, 0, 0, 0.6)',
              pointerEvents: 'none',
              zIndex: 99999,
              willChange: 'transform',
              transition: 'border-color 0.1s ease-out, box-shadow 0.1s ease-out',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {/* Magnified media background */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundImage: `url(${backgroundImage})`,
                backgroundSize: (() => {
                  const mediaElement = mediaRef.current;
                  let mediaAspect, elementAspect;

                  // Get natural aspect ratio for both videos and images
                  if (mediaElement.tagName === 'VIDEO' && mediaElement.videoWidth && mediaElement.videoHeight) {
                    mediaAspect = mediaElement.videoWidth / mediaElement.videoHeight;
                  } else if (mediaElement.tagName === 'IMG' && mediaElement.naturalWidth && mediaElement.naturalHeight) {
                    mediaAspect = mediaElement.naturalWidth / mediaElement.naturalHeight;
                  }

                  if (mediaAspect) {
                    elementAspect = mediaElement.offsetWidth / mediaElement.offsetHeight;

                    // Only adjust if aspect ratios differ significantly
                    if (Math.abs(mediaAspect - elementAspect) > 0.01) {
                      if (mediaAspect > elementAspect) {
                        // Media is wider - letterboxing on top/bottom
                        const renderedHeight = mediaElement.offsetWidth / mediaAspect;
                        return `${mediaElement.offsetWidth * backgroundPosition.effectiveZoom}px ${renderedHeight * backgroundPosition.effectiveZoom}px`;
                      } else {
                        // Media is taller - pillarboxing on left/right
                        const renderedWidth = mediaElement.offsetHeight * mediaAspect;
                        return `${renderedWidth * backgroundPosition.effectiveZoom}px ${mediaElement.offsetHeight * backgroundPosition.effectiveZoom}px`;
                      }
                    }
                  }

                  // Default: aspect ratios match or no natural dimensions
                  return `${mediaElement.offsetWidth * backgroundPosition.effectiveZoom}px ${mediaElement.offsetHeight * backgroundPosition.effectiveZoom}px`;
                })(),
                backgroundPosition: `${backgroundPosition.x}px ${backgroundPosition.y}px`,
                backgroundRepeat: 'no-repeat',
              }}
            />
            {/* Border overlay showing media boundaries */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: (() => {
                  const mediaElement = mediaRef.current;
                  let mediaAspect, elementAspect;

                  if (mediaElement.tagName === 'VIDEO' && mediaElement.videoWidth && mediaElement.videoHeight) {
                    mediaAspect = mediaElement.videoWidth / mediaElement.videoHeight;
                  } else if (mediaElement.tagName === 'IMG' && mediaElement.naturalWidth && mediaElement.naturalHeight) {
                    mediaAspect = mediaElement.naturalWidth / mediaElement.naturalHeight;
                  }

                  if (mediaAspect) {
                    elementAspect = mediaElement.offsetWidth / mediaElement.offsetHeight;
                    if (Math.abs(mediaAspect - elementAspect) > 0.01) {
                      if (mediaAspect > elementAspect) {
                        return `${mediaElement.offsetWidth * backgroundPosition.effectiveZoom}px`;
                      } else {
                        const renderedWidth = mediaElement.offsetHeight * mediaAspect;
                        return `${renderedWidth * backgroundPosition.effectiveZoom}px`;
                      }
                    }
                  }
                  return `${mediaElement.offsetWidth * backgroundPosition.effectiveZoom}px`;
                })(),
                height: (() => {
                  const mediaElement = mediaRef.current;
                  let mediaAspect, elementAspect;

                  if (mediaElement.tagName === 'VIDEO' && mediaElement.videoWidth && mediaElement.videoHeight) {
                    mediaAspect = mediaElement.videoWidth / mediaElement.videoHeight;
                  } else if (mediaElement.tagName === 'IMG' && mediaElement.naturalWidth && mediaElement.naturalHeight) {
                    mediaAspect = mediaElement.naturalWidth / mediaElement.naturalHeight;
                  }

                  if (mediaAspect) {
                    elementAspect = mediaElement.offsetWidth / mediaElement.offsetHeight;
                    if (Math.abs(mediaAspect - elementAspect) > 0.01) {
                      if (mediaAspect > elementAspect) {
                        const renderedHeight = mediaElement.offsetWidth / mediaAspect;
                        return `${renderedHeight * backgroundPosition.effectiveZoom}px`;
                      } else {
                        return `${mediaElement.offsetHeight * backgroundPosition.effectiveZoom}px`;
                      }
                    }
                  }
                  return `${mediaElement.offsetHeight * backgroundPosition.effectiveZoom}px`;
                })(),
                transform: `translate(${backgroundPosition.x}px, ${backgroundPosition.y}px)`,

                boxSizing: 'border-box',
                pointerEvents: 'none',
              }}
            />
          </div>,
          document.body
        )}
    </>
  );
};

export default VideoMagnifier;
